/**
 * 005-packing-verification, admin-packing-oversight bucket. T055 — `GET /oversight/overrides`: the
 * red-band override log (FR-036, §19 audit). Every supervisor override, separate from the variance
 * queue because an override is a supervisor's assertion and an entry is a packer's
 * (data-model.md §3). The entry is named by the API's own order number and SKU name, and the person by
 * `supervisorName`/`packerName` — never an id.
 *
 * The route has no paging, so `PagedTable` pages the whole log in memory and its search box covers
 * every override.
 */
import { useLoaderData, useOutletContext } from "react-router-dom";
import { ErrorSurface, EmptyState } from "@steelyard/theme";
import type { ColumnDef } from "@tanstack/react-table";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { TableSearch } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import { formatMoment } from "../../lib/format";
import { getOversightOverrides, personName, type SupervisorOverrideRow } from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";

type OverrideRow = SupervisorOverrideRow;

export async function overridesLoader() {
  return { overrides: await getOversightOverrides() };
}

/** "ORD-… · SKU name" — the packing entry as a reader knows it; either half the server could not resolve is "—". */
const entryLabel = (row: OverrideRow) => `${row.orderNumber ?? "—"} · ${row.skuName ?? "—"}`;

// Accessors are what the search box matches and a header click sorts — the whole log, since there is no paging.
const columns: ColumnDef<OverrideRow>[] = [
  { id: "occurredAt", accessorKey: "occurredAt", header: "When", cell: ({ row }) => <span className="text-gray-600">{formatMoment(row.original.occurredAt)}</span> },
  { id: "supervisor", accessorFn: (row) => personName(row.supervisorName), header: "Supervisor", cell: ({ row }) => personName(row.original.supervisorName) },
  { id: "packedBy", accessorFn: (row) => personName(row.packerName), header: "Packer", cell: ({ row }) => personName(row.original.packerName) },
  { id: "packingEntry", accessorFn: entryLabel, header: "Packing entry", cell: ({ row }) => entryLabel(row.original) },
  { id: "reason", accessorKey: "reason", header: "Reason", cell: ({ row }) => row.original.reason },
];

export default function PackingOverridesPage() {
  const { overrides } = useLoaderData() as Awaited<ReturnType<typeof overridesLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();

  if (!can(permissionState, "packing.oversight")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "The override log is gated by packing.oversight, which your role does not hold." }}
        action="view the override log"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Supervisor Overrides"
        count={overrides.length}
        description="Every red-band override a supervisor has recorded, with reason, who packed it and timestamp — logged separately from the packing entry it belongs to."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Packing" }, { name: "Overrides" }]}
      />

      <PagedTable<OverrideRow>
        result={{ items: overrides, page: 1, pageSize: overrides.length, totalCount: overrides.length }}
        caption="Supervisor overrides, with time, supervisor, who packed it, packing entry and reason"
        columns={columns}
        search={(table) => <TableSearch table={table} placeholder="Search overrides…" />}
        empty={<EmptyState title="No overrides recorded">No supervisor has overridden a red-band reading yet.</EmptyState>}
      />
    </div>
  );
}
